import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Wrench, Loader2, Download, Mail, Trash2, Plus, RefreshCw, FileText, CheckCircle2, Star, CreditCard, Clock, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../lib/formatUtils';

// Baked-in sensible defaults for AMC quotes. These ship as the initial
// values when ops generates a new AMC quote. If `amc_quote_defaults` is
// configured in Settings (Settings → AMC Quote Defaults), the dialog
// fetches and uses those instead — letting the team curate the standard
// library centrally without a code change.
const DEFAULT_AMC_INCLUSIONS = [
  'Quarterly server health audit + performance report',
  'Security patch & critical OS updates within SLA',
  'Daily off-site backups with 14-day retention',
  'Email & phone support during business hours (Mon–Sat, 10:00–19:00 IST)',
  '4-hour response time for P1 incidents',
  'Uptime monitoring with proactive alerts',
];
const DEFAULT_AMC_EXCLUSIONS = [
  'Hardware replacement or data-centre charges',
  'New feature development beyond minor tweaks',
  'Third-party software / SaaS subscription fees',
  'Migration to a different hosting provider or stack',
  'On-site visits (chargeable separately at actuals)',
];
const DEFAULT_AMC_TERMS = `1. AMC is renewable annually unless terminated with 30-day written notice.
2. Charges are payable in advance per the chosen billing cycle.
3. Response SLAs apply only to issues reported via the official support channel.
4. Items listed under "Exclusions" are billable separately on a time-and-materials basis.
5. All prices are exclusive of applicable taxes unless stated otherwise.`;

// Mirror of routes/amc_quotes.py:CYCLE_MONTHS — single source of truth.
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, half_yearly: 6, annually: 12 };
// Paid AMC always runs on a yearly billing cycle — sub-yearly cycles
// (monthly/quarterly/half-yearly) were a legacy concept we no longer
// surface in the UI. The backend `billing_cycle` field is silently kept
// at 'annually' for schema compatibility; a follow-up migration will
// drop the column entirely.
const FIXED_CYCLE_MONTHS = 12;

const SLA_TIERS = [
  { value: 'basic',    label: 'Basic' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium',  label: 'Premium' },
];
const SERVICE_TYPES = [
  { value: 'amc',      label: 'AMC' },
  { value: 'retainer', label: 'Retainer' },
];

// Year-only ladder. Ops can add/remove rows after the cycle is fixed.
const DEFAULT_LADDER_YEARS = [1, 2, 3];

const blankOption = (years) => ({
  duration_months: years * 12,
  amount_per_cycle: '',
  discount_amount: '',
  is_recommended: false,
  custom_price: false,
});

const _num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const computeOption = (o, taxRate, baseAmountPerYear) => {
  const years = Math.max(1, Math.round(_num(o.duration_months) / 12));
  const effectivePerYear = o.custom_price
    ? _num(o.amount_per_cycle)
    : _num(baseAmountPerYear);
  const subtotal = effectivePerYear * years;
  const afterDisc = Math.max(0, subtotal - _num(o.discount_amount));
  const tax = (afterDisc * _num(taxRate)) / 100;
  return { years, effectivePerYear, subtotal, tax, total: afterDisc + tax };
};

const yearsLabel = (months) => {
  const y = Math.max(1, Math.round(months / 12));
  return `${y} year${y > 1 ? 's' : ''}`;
};

// Backward-compat alias — some places (saved-quote chips, picker
// labels) still imported `monthsLabel`. Now returns the year label too
// so existing UI strings just read "1 year" / "2 years" without
// further changes.
const monthsLabel = yearsLabel;

/**
 * Per-AMC custom quote dialog.
 *
 * Mirrors HostingQuoteDialog UX but adapts to the AMC pricing model:
 *  - billing_cycle drives the cycle-length (monthly/quarterly/half/annual)
 *  - duration_options is a ladder in **months** (always a multiple of the cycle)
 *  - inclusions / exclusions chip-style scope lists
 *  - optional SLA tier
 *
 * Two-tab layout: New Quote builder + Saved (N) list with Download / Email / Convert.
 */
export const AMCQuoteDialog = ({ open, onClose, amc }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('new');
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState({});
  const [emailBusy, setEmailBusy] = useState({});
  const [convertBusy, setConvertBusy] = useState({});
  const [emailDialog, setEmailDialog] = useState({ open: false, quote: null, to: '', cc: '' });
  const [durationPicker, setDurationPicker] = useState({ open: false, quote: null, selected: null });
  const [paylinksByQuote, setPaylinksByQuote] = useState({});

  // Seed billing_cycle from the parent AMC row when available — backend
  const fresh = useMemo(() => {
    // AMC rows expose the client name under different shapes depending
    // on how the record was created — newer rows store it on the row
    // directly, older rows only carry `client_id` and the name needs to
    // be looked up. We prefer the row-level value; the effect below
    // falls back to a client fetch when both are missing.
    const clientName = amc?.client_name || amc?.client?.name || '';
    return {
      title: amc?.title || amc?.project_name || 'AMC / Retainer',
      service_type: (amc?.type === 'retainer' ? 'retainer' : 'amc'),
      sla_tier: 'standard',
      // billing_cycle is permanently 'annually' now — kept in form
      // state so the backend POST payload still matches the schema.
      billing_cycle: 'annually',
      tax_rate: 18,
      notes: '',
      terms: '',
      valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      client_name: clientName,
      client_email: amc?.client_email || amc?.client?.email || '',
      inclusions: [],
      exclusions: [],
      // Single base 1-year amount — drives every non-custom row.
      base_amount_per_year: amc?.amount ? String(amc.amount) : '',
      duration_options: DEFAULT_LADDER_YEARS.map((y, idx) => ({
        ...blankOption(y),
        is_recommended: idx === DEFAULT_LADDER_YEARS.length - 1,
      })),
    };
  }, [amc]);

  const [form, setForm] = useState(fresh);
  const [inclusionInput, setInclusionInput] = useState('');
  const [exclusionInput, setExclusionInput] = useState('');
  // Settings-driven defaults — fetched on mount, fall back to the
  // baked-in DEFAULT_AMC_* constants when nothing is configured.
  const [defaults, setDefaults] = useState({
    inclusions: DEFAULT_AMC_INCLUSIONS,
    exclusions: DEFAULT_AMC_EXCLUSIONS,
    terms: DEFAULT_AMC_TERMS,
  });

  // Empty-input safe setField — defers Number coercion to compute/save.
  const setField = (k) => (e) => setForm(f => ({
    ...f,
    [k]: e?.target ? e.target.value : e,
  }));

  const updateOption = (idx, patch) => {
    setForm(f => ({
      ...f,
      duration_options: f.duration_options.map((o, i) => i === idx ? { ...o, ...patch } : o),
    }));
  };

  const toggleRecommended = (idx) => {
    setForm(f => ({
      ...f,
      duration_options: f.duration_options.map((o, i) => ({ ...o, is_recommended: i === idx })),
    }));
  };

  const addOption = () => {
    setForm(f => {
      const taken = new Set(f.duration_options.map(o => o.duration_months));
      // Pick the next un-used year (1..5y → 12..60mo), matching the
      // backend's 60-month cap.
      let candidate = null;
      for (let y = 1; y <= 5; y += 1) {
        const m = y * 12;
        if (!taken.has(m)) { candidate = y; break; }
      }
      if (candidate == null) {
        toast.error('All tenure slots (up to 5 years) are already added.');
        return f;
      }
      return {
        ...f,
        duration_options: [
          ...f.duration_options,
          { ...blankOption(candidate) },
        ].sort((a, b) => a.duration_months - b.duration_months),
      };
    });
  };

  const removeOption = (idx) => {
    setForm(f => {
      if (f.duration_options.length <= 1) {
        toast.error('At least one duration option is required.');
        return f;
      }
      const next = f.duration_options.filter((_, i) => i !== idx);
      if (!next.some(o => o.is_recommended) && next.length) {
        next[next.length - 1].is_recommended = true;
      }
      return { ...f, duration_options: next };
    });
  };

  // Reset form to fresh state on each open (or when AMC changes).
  useEffect(() => {
    if (open) {
      setForm(fresh);
      setInclusionInput('');
      setExclusionInput('');
    }
  }, [open, fresh]);

  // Fetch the settings-managed defaults exactly once per dialog open.
  // The `amc_quote_defaults` settings key stores either a JSON object
  // {inclusions:[], exclusions:[], terms:""} or — for the textarea-
  // backed Settings card — three sibling keys (amc_default_inclusions /
  // amc_default_exclusions / amc_default_terms). We honour either.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/settings').then(r => {
      if (cancelled) return;
      const s = r.data?.data || {};
      const splitLines = (raw) => String(raw || '')
        .split('\n').map(x => x.trim()).filter(Boolean);
      const incl = s.amc_default_inclusions
        ? splitLines(s.amc_default_inclusions)
        : DEFAULT_AMC_INCLUSIONS;
      const excl = s.amc_default_exclusions
        ? splitLines(s.amc_default_exclusions)
        : DEFAULT_AMC_EXCLUSIONS;
      const terms = (s.amc_default_terms || '').trim() || DEFAULT_AMC_TERMS;
      setDefaults({ inclusions: incl, exclusions: excl, terms });
    }).catch(() => { /* settings unreachable → keep baked-ins */ });
    return () => { cancelled = true; };
  }, [open]);

  // Whenever the dialog opens for a NEW quote, seed inclusions, exclusions
  // and terms with the resolved defaults. Skipped when the form already
  // has data (e.g. the user has typed something — we don't want to
  // overwrite their edits if they re-trigger this effect).
  useEffect(() => {
    if (!open) return;
    setForm(f => ({
      ...f,
      inclusions: f.inclusions.length === 0 ? [...defaults.inclusions] : f.inclusions,
      exclusions: f.exclusions.length === 0 ? [...defaults.exclusions] : f.exclusions,
      terms: f.terms ? f.terms : defaults.terms,
    }));
  }, [open, defaults]);

  // Fallback client-name AND email resolution — old AMCs may lack
  // client_id on the row itself, in which case we walk through the
  // project to find the linked client. We continue the chain until
  // BOTH name AND email are populated (the original bug here was that
  // the guard exited as soon as name was set, leaving email blank
  // even though /clients/{id} would have returned it).
  useEffect(() => {
    if (!open) return;
    if (form.client_name && form.client_email) return;
    let cancelled = false;

    const applyClient = (c) => {
      if (cancelled || !c) return;
      setForm(f => ({
        ...f,
        client_name:  f.client_name  || c.name || c.company_name || '',
        client_email: f.client_email || c.email || '',
      }));
    };

    const run = async () => {
      // Path 1: direct client_id on the AMC row.
      if (amc?.client_id) {
        try {
          const r = await api.get(`/clients/${amc.client_id}`);
          if (r.data?.success) { applyClient(r.data.data); return; }
        } catch { /* fall through */ }
      }
      // Path 2: walk through the project to find the linked client.
      if (amc?.project_id) {
        try {
          const r = await api.get(`/projects/${amc.project_id}`);
          if (r.data?.success) {
            const p = r.data.data || {};
            // Some project payloads carry the client_name inline; if so,
            // use that. Otherwise fetch the linked client by id.
            if (p.client_name && !cancelled) {
              setForm(f => ({
                ...f,
                client_name: f.client_name || p.client_name,
                client_email: f.client_email || p.client_email || '',
              }));
            }
            if (p.client_id) {
              try {
                const cr = await api.get(`/clients/${p.client_id}`);
                if (cr.data?.success) applyClient(cr.data.data);
              } catch { /* ignore */ }
            }
          }
        } catch { /* non-fatal */ }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, amc?.client_id, amc?.project_id, form.client_name, form.client_email]);

  const loadSavedQuotes = useCallback(async () => {
    if (!amc?.id) return;
    setLoadingQuotes(true);
    try {
      const r = await api.get(`/amc/quotes?amc_id=${amc.id}`);
      if (r.data?.success) setSavedQuotes(r.data.data || []);
    } finally {
      setLoadingQuotes(false);
    }
    // Pull payment-link status for ALL quotes on this AMC so we can
    // show 'Paid' / 'Link sent' chips on each saved-quote row.
    try {
      const r = await api.get('/payment-links', {
        params: { entity_type: 'amc', entity_id: amc.id },
      });
      const links = r.data?.data || [];
      const RANK = { paid: 3, created: 2, expired: 1, cancelled: 0 };
      const grouped = {};
      for (const l of links) {
        const qid = l.from_quote_id;
        if (!qid) continue;
        const cur = grouped[qid];
        const curRank = cur ? (RANK[cur.status] ?? -1) : -1;
        const newRank = RANK[l.status] ?? -1;
        if (newRank > curRank ||
            (newRank === curRank && (l.paid_at || l.created_at) > (cur?.paid_at || cur?.created_at || ''))) {
          grouped[qid] = l;
        }
      }
      setPaylinksByQuote(grouped);
    } catch {
      setPaylinksByQuote({});
    }
  }, [amc?.id]);

  useEffect(() => { if (open) loadSavedQuotes(); }, [open, loadSavedQuotes]);

  // Derived per-row totals for the inline preview.
  const optionTotals = useMemo(
    () => form.duration_options.map(o => computeOption(o, form.tax_rate, form.base_amount_per_year)),
    [form.duration_options, form.tax_rate, form.base_amount_per_year],
  );

  const addScope = (kind) => {
    const raw = (kind === 'inclusions' ? inclusionInput : exclusionInput).trim();
    if (!raw) return;
    setForm(f => {
      if (f[kind].includes(raw)) return f;
      return { ...f, [kind]: [...f[kind], raw] };
    });
    if (kind === 'inclusions') setInclusionInput('');
    else setExclusionInput('');
  };

  const removeScope = (kind, item) => {
    setForm(f => ({ ...f, [kind]: f[kind].filter(x => x !== item) }));
  };

  const handleScopeKey = (kind) => (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addScope(kind); }
  };

  const handleSave = async () => {
    if (!form.client_name?.trim()) { toast.error('Client name is required.'); return; }
    const baseAmount = _num(form.base_amount_per_year);
    const effectivePerYear = (o) => o.custom_price ? _num(o.amount_per_cycle) : baseAmount;
    if (baseAmount <= 0 && form.duration_options.every(o => !o.custom_price || _num(o.amount_per_cycle) <= 0)) {
      toast.error('Set a base 1-year price (or a custom per-year on at least one row).');
      return;
    }
    const invalid = form.duration_options.find(o => effectivePerYear(o) <= 0);
    if (invalid) {
      toast.error(`The ${yearsLabel(invalid.duration_months)} option has no price — set the base or click Custom on that row.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_amount_per_year: undefined,  // UI-only — never sent to backend.
        amc_id: amc?.id,
        client_id: amc?.client_id,
        project_id: amc?.project_id,
        tax_rate: _num(form.tax_rate),
        duration_options: form.duration_options.map(o => ({
          duration_months: Number(o.duration_months) || 12,
          amount_per_cycle: effectivePerYear(o),
          discount_amount: _num(o.discount_amount),
          tax_rate: _num(form.tax_rate),
          is_recommended: !!o.is_recommended,
        })),
      };
      const r = await api.post('/amc/quotes', payload);
      if (r.data?.success) {
        toast.success(`Quote ${r.data.data.quote_number} created with ${r.data.data.duration_options.length} duration(s).`);
        await loadSavedQuotes();
        setTab('saved');
      }
    } catch (e) {
      toast.error(extractApiError(e) || 'Failed to save quote.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (quote) => {
    setPdfBusy(b => ({ ...b, [quote.id]: true }));
    try {
      const r = await api.post(`/amc/quotes/${quote.id}/pdf`, null, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `amc-quote-${quote.quote_number || quote.id.slice(-8).toUpperCase()}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      loadSavedQuotes();
    } catch (e) {
      let msg = 'Failed to generate PDF.';
      try {
        const txt = await (e.response?.data?.text ? e.response.data.text() : Promise.resolve(''));
        if (txt) { const parsed = JSON.parse(txt); msg = parsed.detail || msg; }
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setPdfBusy(b => ({ ...b, [quote.id]: false }));
    }
  };

  const openEmailDialog = (quote) => {
    setEmailDialog({ open: true, quote, to: quote.client_email || '', cc: '' });
  };

  const sendEmail = async () => {
    const q = emailDialog.quote;
    if (!emailDialog.to.trim()) { toast.error('Recipient email is required.'); return; }
    setEmailBusy(b => ({ ...b, [q.id]: true }));
    try {
      const r = await api.post(`/amc/quotes/${q.id}/email`, {
        to: emailDialog.to.trim(),
        cc: emailDialog.cc.split(',').map(s => s.trim()).filter(Boolean),
      });
      if (r.data?.success) {
        toast.success('Quote emailed.');
        setEmailDialog({ open: false, quote: null, to: '', cc: '' });
        loadSavedQuotes();
      }
    } catch (e) {
      toast.error(extractApiError(e) || 'Failed to send email.');
    } finally {
      setEmailBusy(b => ({ ...b, [q.id]: false }));
    }
  };

  const handleDelete = async (quote) => {
    if (!window.confirm(`Delete quote ${quote.quote_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/amc/quotes/${quote.id}`);
      toast.success('Quote deleted.');
      loadSavedQuotes();
    } catch (e) {
      toast.error(extractApiError(e) || 'Delete failed.');
    }
  };

  const handleConvertClick = (quote) => {
    if (quote.converted_to_invoice_id) {
      doConvert(quote, null);
      return;
    }
    const opts = quote.duration_options || [];
    if (opts.length > 1) {
      const recommended = opts.find(o => o.is_recommended) || opts[opts.length - 1];
      setDurationPicker({ open: true, quote, selected: recommended.duration_months });
    } else {
      doConvert(quote, opts[0]?.duration_months ?? null);
    }
  };

  const doConvert = async (quote, selectedDurationMonths) => {
    setConvertBusy(b => ({ ...b, [quote.id]: true }));
    try {
      const body = {};
      if (selectedDurationMonths != null) body.selected_duration_months = selectedDurationMonths;
      const r = await api.post(`/amc/quotes/${quote.id}/convert-to-proforma`, body);
      if (r.data?.success) {
        const { invoice_number, already_converted } = r.data.data;
        toast.success(
          already_converted
            ? `Quote was already converted to ${invoice_number}.`
            : `Proforma ${invoice_number} created for the ${monthsLabel(selectedDurationMonths)} option.`,
        );
        await loadSavedQuotes();
        setDurationPicker({ open: false, quote: null, selected: null });
        onClose?.();
        navigate(`/invoices?search=${encodeURIComponent(invoice_number)}`);
      }
    } catch (e) {
      toast.error(extractApiError(e) || 'Failed to create invoice.');
    } finally {
      setConvertBusy(b => ({ ...b, [quote.id]: false }));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="amc-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-primary" />
              AMC / Retainer Quote
              {amc?.type && (
                <Badge variant="outline" className="ml-2 text-xs capitalize">{amc.type}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Share a multi-duration quote (monthly / quarterly / half-yearly / annual cycles).
              The client picks the duration they want; you raise the proforma for that one.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="new" data-testid="amc-quote-tab-new">New Quote</TabsTrigger>
              <TabsTrigger value="saved" data-testid="amc-quote-tab-saved">
                Saved ({savedQuotes.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="space-y-4 pt-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Client name" value={form.client_name} onChange={setField('client_name')} testId="amc-quote-client-name" />
                <Field label="Client email" value={form.client_email} onChange={setField('client_email')} testId="amc-quote-client-email" type="email" />
                <Field label="Quote title" value={form.title} onChange={setField('title')} testId="amc-quote-title" />
                <Field label="Valid until" type="date" value={form.valid_until} onChange={setField('valid_until')} testId="amc-quote-valid-until" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Service type</Label>
                  <Select value={form.service_type} onValueChange={(v) => setForm(f => ({ ...f, service_type: v }))}>
                    <SelectTrigger data-testid="amc-quote-service-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">SLA tier</Label>
                  <Select value={form.sla_tier} onValueChange={(v) => setForm(f => ({ ...f, sla_tier: v }))}>
                    <SelectTrigger data-testid="amc-quote-sla"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SLA_TIERS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tenure options builder */}
              <div className="rounded-md border bg-muted/20 p-3 space-y-3" data-testid="duration-options-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h4 className="text-sm font-semibold">Tenure options</h4>
                    <p className="text-[11px] text-muted-foreground">
                      Type the base 1-year price once — every tenure inherits it.
                      Each row only needs its own discount. Click &ldquo;Custom&rdquo; to override a single row.
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addOption} data-testid="amc-quote-add-duration">
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Add tenure
                  </Button>
                </div>

                {/* Single base-price input — drives every non-custom row */}
                <div className="flex items-center gap-3 p-2.5 rounded-md border border-primary/30 bg-primary/5">
                  <Label className="text-xs font-medium whitespace-nowrap">
                    Base 1-year price (₹)
                  </Label>
                  <Input
                    type="number" min="0"
                    value={form.base_amount_per_year}
                    onChange={setField('base_amount_per_year')}
                    onFocus={(e) => e.target.select()}
                    placeholder="e.g. 5000"
                    className="h-8 max-w-[160px] text-sm font-semibold"
                    data-testid="amc-quote-base-amount"
                  />
                  <p className="text-[10px] text-muted-foreground hidden sm:block">
                    Applied to every row unless that row is set to Custom.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="hidden sm:grid sm:grid-cols-[110px_160px_1fr_120px_56px_36px] gap-2 px-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                    <span>Tenure</span>
                    <span>Per year (₹)</span>
                    <span>Discount (₹)</span>
                    <span className="text-right">Total (₹)</span>
                    <span className="text-center">Best</span>
                    <span></span>
                  </div>
                  {form.duration_options.map((o, idx) => {
                    const t = optionTotals[idx];
                    const effectivePerYearShown = o.custom_price
                      ? o.amount_per_cycle
                      : (form.base_amount_per_year || '');
                    // Years derived from duration_months for display + the
                    // editable year input. We round-trip to months on
                    // change so the backend payload stays canonical.
                    const yearsValue = Math.max(1, Math.round(o.duration_months / 12));
                    return (
                      <div key={`opt-${idx}-${o.duration_months}`}
                           className={`grid grid-cols-2 sm:grid-cols-[110px_160px_1fr_120px_56px_36px] gap-2 items-center p-2 rounded-md border ${o.is_recommended ? 'bg-emerald-500/5 border-emerald-500/40' : 'bg-background border-border'}`}
                           data-testid={`duration-row-${o.duration_months}`}>
                        <div className="text-sm font-medium">
                          <Input type="number" min={1} max={5} step={1} value={yearsValue}
                                 onChange={(e) => {
                                   const y = Math.max(1, Math.min(5, Number(e.target.value) || 1));
                                   updateOption(idx, { duration_months: y * 12 });
                                 }}
                                 data-testid={`duration-${o.duration_months}-months`} className="h-7 text-xs" />
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {yearsValue} year{yearsValue > 1 ? 's' : ''}
                          </p>
                        </div>
                        {/* Per-year cell — read-only when inheriting base */}
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="number" min="0"
                            value={effectivePerYearShown}
                            disabled={!o.custom_price}
                            onChange={(e) => updateOption(idx, { amount_per_cycle: e.target.value })}
                            onFocus={(e) => e.target.select()}
                            data-testid={`duration-${o.duration_months}-amount`}
                            placeholder={o.custom_price ? 'e.g. 7500' : 'inherits base'}
                            className={o.custom_price ? '' : 'opacity-60 cursor-not-allowed'}
                          />
                          <button
                            type="button"
                            onClick={() => updateOption(idx, {
                              custom_price: !o.custom_price,
                              ...(o.custom_price ? {} : {
                                amount_per_cycle: o.amount_per_cycle || form.base_amount_per_year || '',
                              }),
                            })}
                            title={o.custom_price ? 'Reset to base price' : 'Set a different per-year for this tenure'}
                            data-testid={`duration-${o.duration_months}-custom-toggle`}
                            className={`text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap transition-colors ${o.custom_price
                              ? 'bg-amber-500/15 text-amber-700 border-amber-500/40 hover:bg-amber-500/25'
                              : 'text-muted-foreground border-border hover:bg-muted'}`}
                          >
                            {o.custom_price ? '↩︎ Reset' : 'Custom'}
                          </button>
                        </div>
                        <Input type="number" min="0" value={o.discount_amount}
                               onChange={(e) => updateOption(idx, { discount_amount: e.target.value })}
                               onFocus={(e) => e.target.select()}
                               data-testid={`duration-${o.duration_months}-discount`} placeholder="0" />
                        <div className="text-right text-sm font-semibold" data-testid={`duration-${o.duration_months}-total`}>
                          ₹{t.total.toFixed(0)}
                          {t.effectivePerYear > 0 && (
                            <p className="text-[10px] font-normal text-muted-foreground">
                              ₹{(t.total / yearsValue).toFixed(0)}/yr
                            </p>
                          )}
                        </div>
                        <button type="button" onClick={() => toggleRecommended(idx)}
                                className="flex justify-center"
                                title={o.is_recommended ? 'Recommended (BEST VALUE on PDF)' : 'Mark as recommended'}
                                data-testid={`duration-${o.duration_months}-star`}>
                          <Star className={`h-4 w-4 transition-colors ${o.is_recommended ? 'text-emerald-500 fill-emerald-500' : 'text-muted-foreground hover:text-emerald-500/70'}`} />
                        </button>
                        <button type="button" onClick={() => removeOption(idx)}
                                className="flex justify-center text-destructive/70 hover:text-destructive"
                                title="Remove this duration"
                                data-testid={`duration-${o.duration_months}-remove`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Tax rate (%)</Label>
                    <Input type="number" min="0" value={form.tax_rate} onChange={setField('tax_rate')}
                           data-testid="amc-quote-tax" className="w-20 h-8 text-xs" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Applied to every duration. Set 0 if your pricing is already tax-inclusive.
                  </p>
                </div>
              </div>

              {/* Inclusions / Exclusions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ScopeList
                  label="Inclusions" tone="emerald" items={form.inclusions}
                  input={inclusionInput} setInput={setInclusionInput}
                  onAdd={() => addScope('inclusions')} onKey={handleScopeKey('inclusions')}
                  onRemove={(item) => removeScope('inclusions', item)}
                  testIdPrefix="amc-quote-inclusion"
                />
                <ScopeList
                  label="Exclusions" tone="rose" items={form.exclusions}
                  input={exclusionInput} setInput={setExclusionInput}
                  onAdd={() => addScope('exclusions')} onKey={handleScopeKey('exclusions')}
                  onRemove={(item) => removeScope('exclusions', item)}
                  testIdPrefix="amc-quote-exclusion"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Terms (appear on the PDF)</Label>
                  <Textarea rows={3} value={form.terms} onChange={setField('terms')} data-testid="amc-quote-terms"
                            placeholder="Payment terms, response SLAs, exclusions in detail…" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Notes (internal + PDF)</Label>
                  <Textarea rows={3} value={form.notes} onChange={setField('notes')} data-testid="amc-quote-notes"
                            placeholder="Any custom context for the client…" />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="amc-quote-save-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                          : <><Plus className="h-4 w-4 mr-2" />Save Quote</>}
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="saved" className="pt-3">
              {loadingQuotes ? (
                <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
              ) : savedQuotes.length === 0 ? (
                <p className="text-sm text-center text-muted-foreground py-10">No quotes saved yet for this AMC.</p>
              ) : (
                <div className="space-y-2">
                  {savedQuotes.map(q => {
                    const opts = q.duration_options || [];
                    const isMulti = opts.length > 1;
                    return (
                      <div key={q.id} className="rounded-md border p-3 space-y-2" data-testid={`amc-saved-quote-${q.id}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-[200px]">
                            <div className="text-sm font-medium flex items-center gap-1.5">
                              {q.quote_number}
                              {q.converted_to_invoice_number && (
                                <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-500" data-testid={`amc-quote-invoice-link-${q.id}`}>
                                  <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                                  {q.converted_to_invoice_number}
                                  {q.selected_duration_months && <span className="ml-1">· {monthsLabel(q.selected_duration_months)}</span>}
                                </Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              {q.title || 'AMC Quote'} · {q.client_name || '—'}
                              {q.sent_at && <> · <span className="text-emerald-500">Sent {formatDate(q.sent_at)}</span></>}
                            </p>
                            <PaylinkStatusChip link={paylinksByQuote[q.id]} testIdQuoteId={q.id} />
                          </div>
                          <Badge variant={q.status === 'accepted' ? 'default' : q.status === 'sent' ? 'secondary' : 'outline'} className="text-[10px] capitalize">{q.status}</Badge>
                          <Button size="sm" variant="outline" onClick={() => handleDownload(q)} disabled={pdfBusy[q.id]} data-testid={`amc-quote-pdf-${q.id}`}>
                            {pdfBusy[q.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            <span className="ml-1.5">PDF</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEmailDialog(q)} disabled={emailBusy[q.id]} data-testid={`amc-quote-email-${q.id}`}>
                            <Mail className="h-3.5 w-3.5" /><span className="ml-1.5">Email</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleConvertClick(q)}
                                  disabled={convertBusy[q.id]}
                                  title={q.converted_to_invoice_id ? 'Open the linked proforma invoice' : 'Convert to proforma invoice'}
                                  data-testid={`amc-quote-convert-${q.id}`}>
                            {convertBusy[q.id]
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <FileText className="h-3.5 w-3.5" />}
                            <span className="ml-1.5">{q.converted_to_invoice_id ? 'View Invoice' : 'To Invoice'}</span>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(q)} className="text-destructive hover:text-destructive" data-testid={`amc-quote-delete-${q.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {opts.length > 0 && (
                          <div className="flex gap-1.5 flex-wrap pt-1">
                            {opts.map(o => (
                              <Badge key={o.duration_months}
                                     variant={o.is_recommended ? 'default' : 'outline'}
                                     className={`text-[10px] font-medium ${o.is_recommended ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20' : ''}`}
                                     data-testid={`amc-quote-${q.id}-duration-${o.duration_months}`}>
                                {o.is_recommended && <Star className="h-2.5 w-2.5 mr-1 fill-current" />}
                                {monthsLabel(o.duration_months)} · ₹{Number(o.total_amount || 0).toFixed(0)}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {isMulti && q.selected_duration_months && (
                          <p className="text-[10px] text-emerald-500 pt-1">
                            ✓ Client picked the {monthsLabel(q.selected_duration_months)} option.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end pt-2">
                    <Button size="sm" variant="ghost" onClick={loadSavedQuotes} data-testid="amc-quote-refresh">
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Duration picker — shown when converting a multi-duration quote */}
      <Dialog open={durationPicker.open}
              onOpenChange={(o) => { if (!o) setDurationPicker({ open: false, quote: null, selected: null }); }}>
        <DialogContent className="sm:max-w-md" data-testid="duration-picker-dialog">
          <DialogHeader>
            <DialogTitle>Which duration did the client choose?</DialogTitle>
            <DialogDescription>
              Pick the duration the client confirmed. The proforma will be raised for that amount.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(durationPicker.quote?.duration_options || []).map(o => (
              <button key={o.duration_months} type="button"
                      onClick={() => setDurationPicker(s => ({ ...s, selected: o.duration_months }))}
                      data-testid={`duration-picker-${o.duration_months}`}
                      className={`w-full flex items-center justify-between p-3 rounded-md border transition-colors text-left ${durationPicker.selected === o.duration_months
                        ? 'bg-emerald-500/10 border-emerald-500/60'
                        : 'bg-background border-border hover:bg-muted/50'}`}>
                <div>
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {monthsLabel(o.duration_months)} ({o.cycles} {o.cycles === 1 ? 'cycle' : 'cycles'})
                    {o.is_recommended && (
                      <Badge className="text-[9px] bg-emerald-500/20 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20">
                        <Star className="h-2.5 w-2.5 mr-1 fill-current" /> BEST
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    ₹{Number(o.amount_per_cycle || 0).toFixed(0)}/cycle
                    {o.discount_amount > 0 && <> · saves ₹{Number(o.discount_amount).toFixed(0)}</>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold">₹{Number(o.total_amount || 0).toFixed(0)}</p>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDurationPicker({ open: false, quote: null, selected: null })}>Cancel</Button>
            <Button onClick={() => doConvert(durationPicker.quote, durationPicker.selected)}
                    disabled={durationPicker.selected == null || convertBusy[durationPicker.quote?.id]}
                    data-testid="duration-picker-confirm">
              {convertBusy[durationPicker.quote?.id]
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>
                : <><FileText className="h-4 w-4 mr-2" />Create Proforma</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nested email dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => { if (!o) setEmailDialog({ open: false, quote: null, to: '', cc: '' }); }}>
        <DialogContent className="sm:max-w-md" data-testid="amc-quote-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Quote {emailDialog.quote?.quote_number}</DialogTitle>
            <DialogDescription>The PDF will be attached automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="To" value={emailDialog.to} onChange={(e) => setEmailDialog(s => ({ ...s, to: e.target.value }))} testId="amc-quote-email-to" />
            <Field label="Cc (comma separated)" value={emailDialog.cc} onChange={(e) => setEmailDialog(s => ({ ...s, cc: e.target.value }))} testId="amc-quote-email-cc" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, quote: null, to: '', cc: '' })}>Cancel</Button>
            <Button onClick={sendEmail} data-testid="amc-quote-email-send"
                    disabled={emailDialog.quote && emailBusy[emailDialog.quote?.id]}>
              {emailDialog.quote && emailBusy[emailDialog.quote?.id]
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
                : <><Mail className="h-4 w-4 mr-2" />Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value} onChange={onChange} data-testid={testId} />
  </div>
);

// Robust error extraction — FastAPI returns 4xx as `{detail: "string"}`
// and 422 validation errors as `{detail: [{msg, type, loc}, ...]}`. Naive
// rendering of the latter would print "[object Object]" in the toast.
const extractApiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || '';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const first = d[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') return first.msg || JSON.stringify(first);
  }
  if (typeof d === 'object') return d.message || d.msg || JSON.stringify(d);
  return String(d);
};

/**
 * Chip-style scope list for Inclusions / Exclusions. Each entry is a
 * dedupe-checked string. Enter or comma adds; X removes; the list is
 * capped at 50 by the backend so we don't bother capping in the UI.
 */
const ScopeList = ({ label, tone, items, input, setInput, onAdd, onKey, onRemove, testIdPrefix }) => {
  const toneClasses = tone === 'rose'
    ? 'bg-rose-500/10 text-rose-700 border-rose-500/30'
    : 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30';
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={tone === 'rose' ? 'e.g. Database admin out of scope' : 'e.g. 24/7 monitoring'}
          data-testid={`${testIdPrefix}-input`}
        />
        <Button type="button" size="sm" variant="outline" onClick={onAdd} data-testid={`${testIdPrefix}-add`}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {items.map((it, idx) => (
            <span key={`${it}-${idx}`}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border ${toneClasses}`}
                  data-testid={`${testIdPrefix}-${idx}`}>
              {it}
              <button type="button" onClick={() => onRemove(it)} className="hover:opacity-70" aria-label={`Remove ${it}`}>
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Same chip we use on Hosting Quotes — green for paid, amber for awaiting.
 * Cancelled/expired don't render anything.
 */
function PaylinkStatusChip({ link, testIdQuoteId }) {
  if (!link) return null;
  const amt = Number(link.amount || 0).toLocaleString('en-IN');
  const dateIso = link.paid_at || link.created_at || '';
  const datePretty = dateIso
    ? new Date(dateIso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
    : '';
  if (link.status === 'paid') {
    return (
      <span
        className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 font-medium"
        data-testid={`amc-paylink-chip-paid-${testIdQuoteId}`}
        title={`Paid via Razorpay${link.razorpay_payment_id ? ' · ' + link.razorpay_payment_id : ''}`}
      >
        <CheckCircle2 className="h-3 w-3" />
        Paid · ₹{amt}
        {datePretty && <span className="text-emerald-600/80 font-normal">· {datePretty}</span>}
      </span>
    );
  }
  if (link.status === 'created') {
    return (
      <a
        href={link.short_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-700 border border-amber-500/30 font-medium hover:bg-amber-500/15 transition-colors"
        data-testid={`amc-paylink-chip-sent-${testIdQuoteId}`}
        title="Open the payment link in a new tab"
      >
        <CreditCard className="h-3 w-3" />
        Link sent · ₹{amt}
        {datePretty && <span className="text-amber-600/80 font-normal">· {datePretty}</span>}
        <Clock className="h-2.5 w-2.5 opacity-60" />
      </a>
    );
  }
  return null;
}

export default AMCQuoteDialog;
